// BP 56 §3 — the 5-locale list pinned in exactly one constant per surface.
// This file is the frontend's copy: `SUPPORTED_UI_LOCALES`. Marketing,
// extension, and backend each pin their own (astro.config.mjs + src/i18n/
// index.ts, i18n-messages.js header, languageUtil.js UI_LOCALES).
//
// This is the UI-CHROME locale axis only — separate from narration/TTS
// content language, which stays EN/FR/ES (see
// frontend/src/lib/narrationLanguage.ts, untouched by this file).

export const SUPPORTED_UI_LOCALES = ['en', 'fr', 'es', 'da', 'de'] as const;
export type UiLocale = (typeof SUPPORTED_UI_LOCALES)[number];

export const DEFAULT_LOCALE: UiLocale = 'en';

/** Language names always shown in their OWN language (BP 56 §5) — the one
 *  string that is never translated. */
export const LOCALE_NATIVE_NAMES: Record<UiLocale, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  da: 'Dansk',
  de: 'Deutsch',
};

/** Frontend catalog namespaces. `common` + `nav` are WP-0A's; the rest are
 *  filled in by Wave 1 (WP-1A: auth/console/projects/studio/viewer/chat/
 *  learning/settings/billing/errors; WP-1B: admin/platform/reports). Listed
 *  here upfront so `initI18n()` doesn't need editing as each namespace lands
 *  — i18next silently no-ops a `ns` entry with no loaded resources until
 *  someone calls `t()` in it via a lazy `loadNamespaces` (or, simplest, the
 *  page component itself passes its own ns list to `useTranslation`). WP-0A
 *  only ships `common`/`nav` resources; the array below is just the frontend
 *  namespace inventory for `i18n-tools/check-keys.mjs`.
 */
export const FRONTEND_NAMESPACES = [
  'common', 'nav', 'auth', 'console', 'projects', 'studio', 'viewer', 'chat',
  'learning', 'settings', 'billing', 'errors', 'admin', 'platform', 'reports',
  // Leakage-sweep feature namespaces (added 2026-07-17). NOTE: the runtime
  // (runtime.ts) and check-keys both auto-discover namespaces by globbing the
  // locale dir, so this list is documentation only — it does not gate loading.
  'snipboard', 'threads', 'share', 'activity',
  'processStudio',   // BP 26-03
] as const;

/** Namespaces WP-0A actually loads resources for (bundled `en`, lazy others). */
export const OWNED_NAMESPACES = ['common', 'nav'] as const;

export function isSupportedUiLocale(code: unknown): code is UiLocale {
  return typeof code === 'string' && (SUPPORTED_UI_LOCALES as readonly string[]).includes(code);
}
